/**
 * Layer Configuration for Accounts Mapbox
 *
 * Mapbox GL layer specifications for account markers, zones, and mainlines.
 */

import { ACCOUNT_STATUS_COLORS } from '@/constants/accounts/statuses'
import type {
	CircleLayerSpecification, FillLayerSpecification, LineLayerSpecification
} from 'mapbox-gl'
import { useAccountSchema } from './useAccountSchema'
import {
	ACTIVE,
	COMPLETED,
	FAILED_INSTALL,
	HAS_EQUIPMENT_MARKER_FILTER_ID,
	IGNORE,
	INACTIVE,
	LEAD,
	MAINLINE_LINE_STRINGS_LAYER_ID,
	MAINLINE_MARKERS_LAYER_ID,
	MAINLINE_SOURCE_ID,
	MAINLINE_STATUS_COLORS,
	MARKERS_LAYER_ID,
	MARKERS_SOURCE_ID,
	OPPORTUNITY,
	PENDING_ACCEPTANCE,
	PENDING_INSTALL,
	PLANNED,
	PROSPECT,
	VERIFIED,
	ZONES_LAYER_ID,
	ZONES_LINE_LAYER_ID,
	ZONES_SOURCE_ID,
	ZONE_TYPES
} from './constants'

// =============================================================================
// Zone Layers
// =============================================================================

/**
 * Fill layer for zone polygons
 */
export const ZONES_FILL_LAYER: FillLayerSpecification = {
	id: ZONES_LAYER_ID,
	type: 'fill',
	source: ZONES_SOURCE_ID,
	paint: {
		'fill-color': [ 'get', 'color' ],
		'fill-opacity': [
			'match',
			[ 'get', 'zoneType' ],
			ZONE_TYPES.EXTENDED,
			0.25,
			ZONE_TYPES.IN_SERVICE,
			0.25,
			/* Default to invisible */ 0
		]
	},
	layout: { visibility: 'none' }
}

/**
 * Line layer for zone boundaries
 */
export const ZONES_LINE_LAYER: LineLayerSpecification = {
	id: ZONES_LINE_LAYER_ID,
	type: 'line',
	source: ZONES_SOURCE_ID,
	paint: {
		'line-color': [ 'get', 'color' ],
		'line-width': 2,
		'line-opacity': [
			'match',
			[ 'get', 'zoneType' ],
			ZONE_TYPES.EXTENDED,
			1.0,
			ZONE_TYPES.IN_SERVICE,
			0.5,
			/* Default to invisible */ 0
		],
		'line-dasharray': [
			'match',
			[ 'get', 'zoneType' ],
			ZONE_TYPES.EXTENDED,
			[ 2, 4 ],
			ZONE_TYPES.IN_SERVICE,
			[ 1, 0 ],
			[ 1, 0 ] // Default to solid line
		]
	},
	layout: {
		'line-cap': 'round',
		'line-join': 'round',
		'visibility': 'none'
	}
}

// =============================================================================
// Account Marker Layers
// =============================================================================

/**
 * Creates the markers layer specification using the account schema
 * This must be created at runtime to use the schema property keys
 */
export const createMarkersLayer = (): CircleLayerSpecification => {
	const { PROPERTY_KEYS } = useAccountSchema()

	return {
		source: MARKERS_SOURCE_ID,
		id: MARKERS_LAYER_ID,
		type: 'circle',
		paint: {
			'circle-color': [
				'match',
				[ 'get', PROPERTY_KEYS.STATUS ],
				IGNORE,
				ACCOUNT_STATUS_COLORS[IGNORE],
				ACTIVE,
				ACCOUNT_STATUS_COLORS[ACTIVE],
				LEAD,
				ACCOUNT_STATUS_COLORS[LEAD],
				PROSPECT,
				ACCOUNT_STATUS_COLORS[PROSPECT],
				OPPORTUNITY,
				ACCOUNT_STATUS_COLORS[OPPORTUNITY],
				PENDING_INSTALL,
				ACCOUNT_STATUS_COLORS[PENDING_INSTALL],
				PENDING_ACCEPTANCE,
				ACCOUNT_STATUS_COLORS[PENDING_ACCEPTANCE],
				INACTIVE,
				ACCOUNT_STATUS_COLORS[INACTIVE],
				FAILED_INSTALL,
				ACCOUNT_STATUS_COLORS[FAILED_INSTALL],
				/* default */ '#000000'
			],
			'circle-radius': 5,
			'circle-stroke-width': 2,
			'circle-stroke-color': '#8596ad'
		},
		filter: [ '!', [ 'has', 'point_count' ] ],
		layout: { visibility: 'visible' }
	}
}

/**
 * Legacy export for backward compatibility - will be removed in Phase 5
 * @deprecated Use createMarkersLayer() instead
 */
export const MARKERS_LAYER = createMarkersLayer()

// =============================================================================
// Mainline Layers
// =============================================================================

/**
 * Circle layer for mainline handhole markers
 */
export const MAINLINE_MARKERS_LAYER: CircleLayerSpecification = {
	source: MAINLINE_SOURCE_ID,
	id: MAINLINE_MARKERS_LAYER_ID,
	type: 'circle',
	paint: {
		'circle-color': [
			'match',
			[ 'get', 'status' ],
			PLANNED,
			MAINLINE_STATUS_COLORS.PLANNED,
			COMPLETED,
			MAINLINE_STATUS_COLORS.COMPLETED,
			VERIFIED,
			MAINLINE_STATUS_COLORS.VERIFIED,
			/* default */ '#000000'
		],
		'circle-radius': 5,
		'circle-stroke-width': 2,
		'circle-stroke-color': '#8596ad'
	},
	filter: [
		'all',
		[ '==', 'category_name', 'Handhole' ],
		[ '==', '$type', 'Point' ]
	],
	layout: { visibility: 'none' }
}

/**
 * Line layer for mainline cable runs
 */
export const MAINLINE_LINE_STRINGS_LAYER: LineLayerSpecification = {
	source: MAINLINE_SOURCE_ID,
	id: MAINLINE_LINE_STRINGS_LAYER_ID,
	type: 'line',
	paint: {
		'line-color': [
			'match',
			[ 'get', 'status' ],
			PLANNED,
			MAINLINE_STATUS_COLORS.PLANNED,
			COMPLETED,
			MAINLINE_STATUS_COLORS.COMPLETED,
			VERIFIED,
			MAINLINE_STATUS_COLORS.VERIFIED,
			/* default */ '#000000'
		],
		'line-width': 5,
		'line-dasharray': [
			'match',
			[ 'get', 'status' ],
			// Dash pattern for planned line
			PLANNED,
			[ 2, 4 ],
			// Solid line for completed line
			COMPLETED,
			[ 1, 0 ],
			VERIFIED,
			[ 1, 0 ], // Solid line for verified line
			[ 1, 0 ] // Default to solid line
		]
	},
	filter: [
		'all',
		[ '==', '$type', 'LineString' ] // Filter for LineString geometries
	],
	layout: {
		'line-cap': 'round',
		'visibility': 'none'
	}
}

// =============================================================================
// Layer Filters
// =============================================================================

/**
 * Creates the equipment filter configuration using the account schema
 * This must be created at runtime to use the schema property keys
 */
export const createHasEquipmentMarkerLayerFilter = () => {
	const { PROPERTY_KEYS } = useAccountSchema()

	return {
		layerId: MARKERS_LAYER_ID,
		filterId: HAS_EQUIPMENT_MARKER_FILTER_ID,
		expression: [ '==', [ 'get', PROPERTY_KEYS.HAS_EQUIPMENT ], 'true' ],
		visibleLayerIds: [ MARKERS_LAYER_ID, ZONES_LAYER_ID, ZONES_LINE_LAYER_ID ]
	}
}

/**
 * Legacy export for backward compatibility - will be removed in Phase 5
 * @deprecated Use createHasEquipmentMarkerLayerFilter() instead
 */
export const HAS_EQUIPMENT_MARKER_LAYER_FILTER = createHasEquipmentMarkerLayerFilter()
